// Global incoming-call layer (Synex Calling V2, Phases 1/3/4/5/7/9).
//
// Mounted ONCE inside the Router (App.js), this provider makes incoming Audio Calls, Video
// Calls, and Huddles reachable from ANY page of SynexOS without the Chat module being open.
// It reuses the EXISTING signaling (the `/api/workspace/ws/workspace` unified socket and the
// same event/type names), the EXISTING ringtone (soundManager), the EXISTING busy store
// (callState), and the EXISTING meeting UI (the standalone `/connect/:meetingId` room).
//
// Single-instance guarantees (Phase 7):
//   • One persistent WebSocket per session — opened only while authenticated AND while the
//     Chat module is NOT mounted (WorkspaceChats owns the socket when it is on screen, so we
//     never run two). See utils/callPresence.
//   • One banner + one ringtone — soundManager keeps a single ring interval; the provider
//     shows at most one banner and a module-level owner guard blocks a second provider.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { workspaceSocket } from "../utils/workspaceSocket";
import { authService } from "../utils/auth";
import { soundManager } from "../utils/soundManager";
import { isBusy as isCallBusy } from "../modules/connect/calling/callState";
import {
  decideCallEvent,
  decideRepeatOffer,
  flushQueuedCallSignals,
  incomingFields,
  isForOtherIncoming,
  queueCallSignal,
} from "../modules/connect/calling/callIdentity";
import { notifyBannerCallTerminated } from "../modules/connect/calling/bannerCallLifecycle";
import {
  callKey,
  claimRing,
  claimAnswer,
  resolveCall,
  releaseRing,
  isAnotherTabCallBusy,
  subscribeCallCoordination,
  writeCallHandoff,
} from "../utils/callCoordinator";
import { openInNewTab } from "../utils/openInNewTab";
import { messageEventId } from "../utils/commBadgeEvents";
import { isChatModuleActive, subscribeChatModule } from "../utils/callPresence";
import directoryService from "../modules/connect/services/directoryService";
import IncomingCallBanner from "../components/calling/IncomingCallBanner";
import { logWsDiagnostic } from "../utils/blankPageDiagnostics";

// Auto-dismiss a ringing banner as a missed call if it is never answered (caller usually
// cancels first via call_end; this is the safety net for a dropped cancel signal).
const RING_TIMEOUT_MS = 35000;

const IncomingCallContext = createContext(null);
export const useIncomingCall = () => useContext(IncomingCallContext);

// Module-level owner guard — only the first mounted provider runs the global listener.
let PROVIDER_OWNER = null;

export function IncomingCallProvider({ children }) {
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState(null);

  const incomingRef = useRef(null);     // latest banner (closure-safe)
  const answeredRef = useRef(null);     // { call_instance_id } this tab answered (F-06)
  const ringTimeoutRef = useRef(null);
  const directoryRef = useRef(null);    // cached { user_id: full_name }
  const ownerKeyRef = useRef({});       // identity token for the owner guard

  const setIncomingSafe = useCallback((val) => {
    incomingRef.current = val;
    setIncoming(val);
  }, []);

  const stopRing = useCallback(() => {
    soundManager.stopRingtone();
    clearTimeout(ringTimeoutRef.current);
  }, []);

  // `outcome` resolves the call browser-wide (other tabs dismiss too). Pass null when the
  // dismissal itself came from another tab's resolution, to avoid re-broadcasting it.
  const dismiss = useCallback((outcome = "ended") => {
    const inc = incomingRef.current;
    stopRing();
    setIncomingSafe(null);
    if (inc?.key) {
      if (outcome) resolveCall(inc.key, outcome);
      else releaseRing(inc.key);
    }
  }, [stopRing, setIncomingSafe]);

  const sendSignal = useCallback((payload) => {
    const sent = workspaceSocket.send(payload);
    // Socket down: instance-bound answer / reject / busy / end wait for the reconnect (the
    // backend applies them to that exact instance or ignores them); legacy ones drop as before.
    if (!sent) queueCallSignal(payload);
    return sent;
  }, []);

  // Best-effort caller name resolution (cached). The ring payload carries only an id.
  const resolveName = useCallback(async (userId) => {
    if (!userId) return "Someone";
    const cached = directoryRef.current?.[userId];
    if (cached) return cached;
    try {
      const members = await directoryService.searchMembers("", 200);
      const map = {};
      (members || []).forEach((m) => {
        if (m.user_id) map[m.user_id] = m.full_name || m.email || m.user_id;
      });
      directoryRef.current = map;
      return map[userId] || "Someone";
    } catch {
      return "Someone";
    }
  }, []);

  // Show a banner + start the ringtone for a new incoming call/huddle.
  const present = useCallback(
    (call) => {
      setIncomingSafe(call);
      soundManager.startRingtone("incoming"); // stopRingtone() inside guarantees a single ring
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = setTimeout(() => dismiss("missed"), RING_TIMEOUT_MS);

      const id = call.from_user_id || call.host_id;
      resolveName(id).then((name) => {
        // Only patch if the same call is still ringing.
        if (incomingRef.current && (incomingRef.current.from_user_id || incomingRef.current.host_id) === id) {
          setIncomingSafe({ ...incomingRef.current, name });
        }
      });
    },
    [dismiss, resolveName, setIncomingSafe]
  );

  // ── Inbound signaling (mirrors WorkspaceChats' unified-socket handler) ──────────────
  const handleMessage = useCallback(
    (data) => {
      const me = authService.getUser()?.id;
      if (!me) return;
      const evt = data.event;

      if (evt === "call_offer") {
        if (data.to_user_id !== me) return;
        const ringing = incomingRef.current;
        // A NEWER attempt from the caller already ringing (the backend superseded the ringing
        // one): the banner now refers to the new instance, so Answer / Decline act on it.
        if (ringing && ringing.kind === "call" && decideRepeatOffer(data, ringing) === "supersede") {
          setIncomingSafe({
            ...ringing,
            call_instance_id: data.call_instance_id,
            meeting_id: data.meeting_id || ringing.meeting_id,
            call_type: data.call_type || ringing.call_type,
          });
          return;
        }
        // Duplicate offer from the same caller already ringing → ignore.
        if (incomingRef.current && incomingRef.current.from_user_id === data.from_user_id) return;
        // Busy (already in / handling a call) → tell the caller, exactly like the legacy path.
        const key = callKey({
          kind: "call",
          chat_id: data.chat_id,
          from_user_id: data.from_user_id,
          meeting_id: data.meeting_id,
          call_instance_id: data.call_instance_id,
        });
        if (isCallBusy() || incomingRef.current) {
          sendSignal({ type: "call_busy", chat_id: data.chat_id, to_user_id: data.from_user_id, ...incomingFields(data) });
          // This tab answered for the whole browser; keep other tabs from ringing.
          resolveCall(key, "busy");
          return;
        }
        const offerAt = Date.now();
        const call = {
          kind: "call",
          key,
          offerAt,
          from_user_id: data.from_user_id,
          call_type: data.call_type || "audio",
          meeting_id: data.meeting_id,
          chat_id: data.chat_id,
          name: null,
          ...incomingFields(data), // call_instance_id (v8): Answer / Decline bind to it
        };
        // Cross-tab: exactly one tab rings. A tab that is in another call (busy lease)
        // replies call_busy itself, so the owner stays silent in that case.
        const presentIfOwner = () => {
          if (incomingRef.current) return releaseRing(key);
          if (isAnotherTabCallBusy()) return releaseRing(key);
          present(call);
        };
        claimRing(key, { ttlMs: RING_TIMEOUT_MS, onTakeover: presentIfOwner, offerAt }).then((owned) => {
          if (owned) presentIfOwner();
        });
      } else if (evt === "huddle_start") {
        // A huddle "rings" its chat members; ignore our own and ignore while busy.
        if (!data.host_id || String(data.host_id) === String(me)) return;
        if (isCallBusy() || incomingRef.current) return;
        const key = callKey({ kind: "huddle", chat_id: data.chat_id, host_id: data.host_id });
        const offerAt = Date.now();
        const call = { kind: "huddle", key, offerAt, host_id: data.host_id, chat_id: data.chat_id, name: null };
        const presentIfOwner = () => {
          if (incomingRef.current || isAnotherTabCallBusy()) return releaseRing(key);
          present(call);
        };
        claimRing(key, { ttlMs: RING_TIMEOUT_MS, onTakeover: presentIfOwner, offerAt }).then((owned) => {
          if (owned) presentIfOwner();
        });
      } else if (evt === "call_reject" || evt === "call_end" || evt === "call_busy") {
        // F-06: the answer this tab sent lost to another device of the same user (the backend
        // replies only to the answering connection). The room it opened must leave locally —
        // never hang up the call the winning device is in.
        const answered = answeredRef.current;
        if (
          evt === "call_end" &&
          String(data.reason || "") === "answered_elsewhere" &&
          answered &&
          data.call_instance_id &&
          String(data.call_instance_id) === String(answered.call_instance_id)
        ) {
          answeredRef.current = null;
          notifyBannerCallTerminated(answered.call_instance_id, "answered_elsewhere");
          return;
        }
        // Caller cancelled / ended / busy while we were ringing → clear the banner (never for an
        // event explicitly addressed to a different call instance).
        const inc = incomingRef.current;
        if (
          inc &&
          inc.kind === "call" &&
          (inc.from_user_id === data.from_user_id || inc.chat_id === data.chat_id) &&
          !isForOtherIncoming(data, inc)
        ) {
          dismiss();
        }
      } else if (evt === "call_state_sync" || evt === "call_answered_elsewhere") {
        // v8: the ringing call was resolved (cancelled / missed / answered on another device).
        const inc = incomingRef.current;
        if (inc && inc.kind === "call") {
          const decision = decideCallEvent(data, { incoming: inc });
          // Resolve (not release): no other tab may take over ringing a call that is over here.
          if (decision.action === "end" || decision.action === "dismiss_incoming") dismiss();
        }
      } else if (evt === "huddle_end") {
        const inc = incomingRef.current;
        if (inc && inc.kind === "huddle" && inc.chat_id === data.chat_id) dismiss();
      } else if (evt === "message_created") {
        const msg = data.message;
        if (msg && msg.sender_id !== me) {
          if (data.channel_id) {
            // Post to badge broadcast channel only if NOT currently on the channels page (to avoid double-counting)
            if (!window.location.pathname.startsWith("/workspace/channels")) {
              try {
                const bc = new BroadcastChannel("comm_badge_events");
                bc.postMessage({ type: "new_channel_message", eventId: messageEventId(msg, data.channel_id) });
                bc.close();
              } catch (_) {}
            }
          } else {
            // Post to badge broadcast channel only if NOT currently on the chats page
            if (!window.location.pathname.startsWith("/workspace/chats")) {
              try {
                const bc = new BroadcastChannel("comm_badge_events");
                bc.postMessage({ type: "new_chat_message", eventId: messageEventId(msg, data.chat_id) });
                bc.close();
              } catch (_) {}
            }
          }
        }
      }
    },
    [present, dismiss, sendSignal, setIncomingSafe]
  );

  // Keep the handler in a ref so the socket effect can run once (no reconnect churn).
  const handleMessageRef = useRef(handleMessage);
  handleMessageRef.current = handleMessage;

  // ── Actions (Phase 4/5) ─────────────────────────────────────────────────────────────
  const answer = useCallback(() => {
    const inc = incomingRef.current;
    if (!inc) return;
    stopRing();

    // Cross-tab: refuse if any tab already answered/declined this call, otherwise mark it
    // answered before signalling so no other tab can send a second call_answer.
    if (inc.key && !claimAnswer(inc.key, inc.offerAt)) {
      setIncomingSafe(null);
      return;
    }

    if (inc.kind === "huddle") {
      // Huddle media is Chat-module-internal (mesh) — reuse it by deep-linking into the chat.
      setIncomingSafe(null);
      navigate(`/workspace/chats?chat=${encodeURIComponent(inc.chat_id || "")}`);
      return;
    }

    // 1:1 call — accept on the control plane, then open the existing meeting UI.
    sendSignal({ type: "call_answer", chat_id: inc.chat_id, to_user_id: inc.from_user_id, ...incomingFields(inc) });
    answeredRef.current = inc.call_instance_id ? { call_instance_id: inc.call_instance_id } : null;
    setIncomingSafe(null);

    if (inc.meeting_id) {
      // Reuse the standalone LiveKit room. The "busy" state belongs to the tab that hosts
      // the meeting (it is adopted there on connect and cleared on leave), NOT to this tab —
      // marking this tab busy left it auto-rejecting every later call until reload (H2).
      writeCallHandoff(inc.meeting_id, {
        meeting_id: inc.meeting_id,
        call_type: inc.call_type,
        role: "callee",
        source_type: "chat",
        source_id: inc.chat_id,
        // F-08: the room tab binds its lifecycle (heartbeat, hang-up, remote end) to this exact
        // call instance. Absent for a legacy ring (no instance): the room is then a plain meeting.
        ...(inc.call_instance_id
          ? { chat_id: inc.chat_id, peer_user_id: inc.from_user_id, ...incomingFields(inc) }
          : {}),
      });
      const target = `/connect/${inc.meeting_id}`;
      if (!openInNewTab(target)) {
        // Popup blocked: join in this tab instead of silently losing the answered call.
        navigate(target);
      }
    } else {
      // Legacy (no LiveKit session on the ring) → hand off to the Chat module's accept flow.
      navigate(`/workspace/chats?chat=${encodeURIComponent(inc.chat_id || "")}`);
    }
  }, [navigate, sendSignal, stopRing, setIncomingSafe]);

  const decline = useCallback(() => {
    const inc = incomingRef.current;
    if (!inc) return;
    if (inc.kind === "call") {
      sendSignal({ type: "call_reject", chat_id: inc.chat_id, to_user_id: inc.from_user_id, ...incomingFields(inc) });
    }
    dismiss("declined");
  }, [sendSignal, dismiss]);

  // Another tab resolved (answered / declined / ended) the call this tab is showing.
  useEffect(
    () =>
      subscribeCallCoordination((msg) => {
        if (msg?.type !== "resolved") return;
        const inc = incomingRef.current;
        if (inc?.key && inc.key === msg.key) dismiss(null);
      }),
    [dismiss]
  );

  // ── Single global socket, gated on auth + Chat-not-mounted (Phases 1/7/9) ───────────
  useEffect(() => {
    // Owner guard: a second provider instance stays passive.
    if (PROVIDER_OWNER && PROVIDER_OWNER !== ownerKeyRef.current) return;
    PROVIDER_OWNER = ownerKeyRef.current;

    let destroyed = false;
    let chatActive = isChatModuleActive();
    let unsubSocket = null;

    const attach = () => {
      if (destroyed || chatActive || unsubSocket) return;
      const token = authService.getToken();
      if (!token || !authService.isAuthenticated()) return;

      logWsDiagnostic("workspace_incoming_calls", "attaching_to_shared_socket");
      unsubSocket = workspaceSocket.subscribe((parsed) => {
        if (
          parsed?.type === "workspace.notification.created" ||
          parsed?.event === "workspace.notification.created" ||
          parsed?.event === "notification_created"
        ) {
          window.dispatchEvent(
            new CustomEvent("workspace-notification-received", { detail: parsed })
          );
        }
        handleMessageRef.current(parsed);
      });
    };

    const detach = () => {
      if (unsubSocket) {
        logWsDiagnostic("workspace_incoming_calls", "detaching_from_shared_socket");
        unsubSocket();
        unsubSocket = null;
      }
    };

    // When Chat mounts/unmounts, hand the listener + banner ownership back and forth.
    const unsub = subscribeChatModule((active) => {
      chatActive = active;
      if (active) {
        detach();
        // Hand-over to the Chat module in this tab, not a resolution of the call.
        dismiss(null);
      } else {
        attach();
      }
    });

    attach();

    // Reconnect recovery: queued instance-bound call signals go out as soon as the shared
    // socket is open again (the backend then sends call_state_sync for any live call).
    const unsubStatus = workspaceSocket.subscribeStatus((connected) => {
      if (connected && !chatActive) flushQueuedCallSignals((p) => workspaceSocket.send(p));
    });

    return () => {
      destroyed = true;
      unsub();
      unsubStatus();
      detach();
      stopRing();
      if (PROVIDER_OWNER === ownerKeyRef.current) PROVIDER_OWNER = null;
    };
    // dismiss/stopRing are stable (useCallback); effect runs once for the lifetime of the owner.
  }, [dismiss, stopRing]);

  return (
    <IncomingCallContext.Provider value={{ incoming, answer, decline }}>
      {children}
      <IncomingCallBanner call={incoming} onAnswer={answer} onDecline={decline} />
    </IncomingCallContext.Provider>
  );
}

export default IncomingCallProvider;
